/**
 * db-viewer.component.ts
 * UI layer - handles user interactions and delegates to service
 */
import { Component, OnInit, AfterViewInit, OnDestroy, HostListener, input, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, debounceTime } from 'rxjs';
import { TabSdk } from '@techextensor/tab-sdk';
import { AppObject, GridUpdateState, QueryExecutionResponse, UIQueryParameter, ValidationError } from '../../../core/interfaces/dev-tool/db-viewer.interface';
import { TabGridComponent } from '../../../features-modules/formio/custom-components/tab-grid/tab-grid.component';
import { VisualQueryBuilderComponent } from './components/visual-query-builder/visual-query-builder.component';
import { SchemaExplorerComponent } from './components/schema-explorer/schema-explorer.component';
import { SplitterModule } from '@syncfusion/ej2-angular-layouts';
import { TabCodeEditorModule } from '../../../features-modules/formio/custom-components/tab-code-editor/tab-code-editor.module';
import { SqlCompletionProvider } from '../../../core/providers/sql-completion.provider';
import { DbViewerService } from '../../../core/services/dev-tool/db-viewer.service';
import { SqlParserService } from '../../../core/services/dev-tool/sql-parser.service';

@Component({
  selector: 'app-db-viewer',
  imports: [
    CommonModule,
    FormsModule,
    TabGridComponent,
    SplitterModule,
    VisualQueryBuilderComponent,
    SchemaExplorerComponent,
    TabCodeEditorModule
  ],
  templateUrl: './db-viewer.component.html',
  styleUrl: './db-viewer.component.scss'
})
export class DbViewerComponent implements OnInit, AfterViewInit, OnDestroy {

  contentHeight = input<number>(500);

  // Monaco editor
  private editor: any = null;
  private monaco: any;
  private completionProvider: SqlCompletionProvider | null = null;
  private completionProviderDisposable: any = null;

  private editorPosition = signal<{ lineNumber: number; column: number } | null>(null);
  lineAndColumn = computed(() => {
    const pos = this.editorPosition();
    return pos ? `Ln ${pos.lineNumber}, Col ${pos.column}` : '';
  });

  // Monaco editor options
  editorOptions = {
    language: 'sql',
    theme: 'vs-dark',
    automaticLayout: true,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    fontSize: 13,
    lineNumbers: 'on' as const,
    roundedSelection: false,
    scrollbar: {
      verticalScrollbarSize: 10,
      horizontalScrollbarSize: 10
    },
    tabSize: 2,
    insertSpaces: true,
    wordWrap: 'on' as const,
    wrappingIndent: 'indent' as const
  };

  // UI State
  activeTab: 'sql' | 'visual' | 'json' = 'sql';
  sqlQuery: string = '';
  forceVisualParse: boolean = false;
  jsonInput: string = '';
  parameters: UIQueryParameter[] = [];
  isExecuting: boolean = false;

  // Validation
  validationErrors: ValidationError[] = [];
  hasValidationErrors: boolean = false;

  // Query results
  queryResults: QueryExecutionResponse | null = null;
  showResults: boolean = false;

  // Schema data
  private schemaData: { appObjects: AppObject[] } | null = null;

  // Cleanup
  private destroy$ = new Subject<void>();
  private queryChangeSubject = new Subject<string>();

  get gridConfig(): any {
    return {
      component: {
        key: 'db-viewer-results-grid',
        label: 'Query Results',
        Configuration: {
          componentValue: {
            mode: 'static',
            staticData: this.queryResults?.data || [],
            isshowSearch: true,
            // isAllowFilter: true,
            isShowColumChooser: true,
            isSortable: true,
            // isShowColumnMenu: true,
            isAllowResizing: true,
            isAllowReorder: true,
            isPageable: true,
            isAllowGrouping: true,
            pageSize: 25,
            gridHeight: '500px',
          }
        }
      },
      componentValue: {},
      otherFormdata: {},
      parameters: {}
    };
  }


  /**
   * Handle F5 key press to execute query
   * If text is selected, execute only the selected portion
   * Otherwise, execute the full query
   */
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: any): void {
    if (event.key === 'F5' || event.keyCode === 116) {
      event.preventDefault();
      event.stopPropagation();
      this.executeQueryFromButton();
    }
  }

  constructor(
    private dbViewerService: DbViewerService,
    private sqlParserService: SqlParserService
  ) { }

  ngOnInit(): void {
    this.dbViewerService.setOriginalQuery(this.sqlQuery);

    // Setup debounced validation
    this.queryChangeSubject.pipe(
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe(query => {
      this.validateQuery(query);
    });
  }

  ngAfterViewInit(): void {
    this.loadSchemaForAutocomplete();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.completionProviderDisposable) {
      this.completionProviderDisposable.dispose();
    }
    if (this.editor) {
      this.editor.dispose();
      this.editor = null;
    }
  }

  // ==================== MONACO EDITOR ====================

  onEditorInit(editor: any): void {
    this.editor = editor;
    this.monaco = (window as any).monaco;

    if (!this.monaco) {
      console.error('Monaco not available on window');
      return;
    }

    // Register SQL language if not already registered
    if (!this.monaco.languages.getLanguages().find((lang: any) => lang.id === 'sql')) {
      this.monaco.languages.register({ id: 'sql' });
      this.registerSQLSyntaxHighlighting();
    }

    this.editor.onDidChangeCursorPosition((e: any) => {
      this.editorPosition.set({
        lineNumber: e.position.lineNumber,
        column: e.position.column
      });
    });

    // Trigger suggestions on every keystroke
    this.editor.onKeyDown((e: any) => {
      const isCharacterKey = e.keyCode >= 48 && e.keyCode <= 90;
      const isBackspace = e.keyCode === this.monaco.KeyCode.Backspace;
      const isDelete = e.keyCode === this.monaco.KeyCode.Delete;

      if (isCharacterKey || isBackspace || isDelete) {
        setTimeout(() => {
          if (this.editor) {
            const position = this.editor.getPosition();
            if (position) {
              const currentLine = this.editor.getModel()?.getLineContent(position.lineNumber) || '';
              const lineBeforeCursor = currentLine.substring(0, position.column - 1);
              const wordMatch = lineBeforeCursor.match(/(\w+)$/);
              if (wordMatch && wordMatch[1] && wordMatch[1].length >= 1) {
                this.editor.trigger('keyboard', 'editor.action.triggerSuggest', {});
              }
            }
          }
        }, 10);
      }
    });

    // Listen to content changes
    this.editor.onDidChangeModelContent(() => {
      const value = this.editor?.getValue() || '';
      this.sqlQuery = value;
      this.onQueryChange();
    });

    // Initialize completion provider
    this.initializeCompletionProvider();
  }

  /**
   * Register SQL syntax highlighting
   */
  private registerSQLSyntaxHighlighting(): void {
    this.monaco.languages.setMonarchTokensProvider('sql', {
      tokenizer: {
        root: [
          [/\b(SELECT|FROM|WHERE|JOIN|INNER|LEFT|RIGHT|FULL|OUTER|CROSS|ON|AS|AND|OR|NOT|IN|LIKE|BETWEEN|IS|NULL|GROUP|BY|ORDER|HAVING|LIMIT|OFFSET|INSERT|INTO|VALUES|UPDATE|SET|DELETE|UNION|ALL|DISTINCT|COUNT|SUM|AVG|MAX|MIN|CASE|WHEN|THEN|ELSE|END|ASC|DESC|EXISTS|ANY|SOME)\b/i, 'keyword'],
          [/--.*$/, 'comment'],
          [/\/\*[\s\S]*?\*\//, 'comment'],
          [/'([^'\\]|\\.)*'/, 'string'],
          [/"/, 'string', '@doubleString'],
          [/\d+\.?\d*/, 'number'],
          [/[=<>!]+/, 'operator'],
          [/[a-zA-Z_][a-zA-Z0-9_]*/, 'identifier']
        ],
        doubleString: [
          [/[^\\"]+/, 'string'],
          [/"/, 'string', '@pop']
        ]
      }
    });
  }

  /**
   * Initialize completion provider
   */
  private initializeCompletionProvider(): void {
    if (this.completionProviderDisposable) {
      this.completionProviderDisposable.dispose();
    }

    const tableNames = this.schemaData?.appObjects?.map(obj => obj.name) || [];
    const schemaMap = new Map<string, string[]>();
    this.schemaData?.appObjects?.forEach(appObject => {
      const fieldNames = appObject.fields.map(field => field.name);
      schemaMap.set(appObject.name.toLowerCase(), fieldNames);
    });

    this.completionProvider = new SqlCompletionProvider(tableNames, schemaMap);
    this.completionProviderDisposable = this.monaco.languages.registerCompletionItemProvider('sql', this.completionProvider);
  }

  /**
   * Load schema for autocomplete
   */
  private loadSchemaForAutocomplete(): void {
    try {
      const appObjects = TabSdk.metadataUtils.getAllAppObjects();

      const convertedAppObjects: AppObject[] = appObjects.map((appObj: any) => ({
        id: appObj.ID,
        name: appObj.ObjectName,
        displayName: appObj.DisplayName,
        fields: appObj.fields().map((field: any) => ({
          id: field.ID,
          name: field.FieldName,
          displayName: field.DisplayName,
          dataType: field.FieldType?.DataType || 0,
          isPrimaryKey: field.IsPrimaryKey || false,
          isRequired: field.IsRequired || false,
          isLookup: field.FieldType?.DataType === 17,
          lookupDetails: field.FieldType?.DataType === 17 && field.LookUpDetails ? {
            targetObjectName: field.LookUpDetails.LookupAppObjectName,
            targetDisplayField: field.LookUpDetails.LookupAppObjectDisplayFieldName
          } : undefined
        }))
      }));

      this.schemaData = { appObjects: convertedAppObjects };
    } catch (error) {
      console.error('Error loading schema for autocomplete:', error);
      TabSdk.ui.showError({ message: 'Failed to load database schema for autocomplete' });
    }
  }

  // ==================== QUERY OPERATIONS ====================

  onQueryChange(): void {
    // Update Monaco editor if needed
    if (this.editor && this.activeTab === 'sql') {
      const editorValue = this.editor.getValue();
      if (editorValue !== this.sqlQuery) {
        this.updateEditorValue(this.sqlQuery);
      }
    }

    // Update original query if no grid modifications
    if (!this.dbViewerService.hasGridModifications()) {
      this.dbViewerService.setOriginalQuery(this.sqlQuery);
    }

    // Detect parameters
    this.parameters = this.dbViewerService.detectParameters(this.sqlQuery, this.parameters);

    // Trigger validation
    this.queryChangeSubject.next(this.sqlQuery);
  }

  validateQuery(query: string): void {
    this.validationErrors = this.dbViewerService.validateQuery(query);
    this.hasValidationErrors = this.validationErrors.length > 0;
    this.updateValidationMarkers();
  }

  formatQuery(): void {
    if (!this.sqlQuery || !this.sqlQuery.trim()) {
      TabSdk.ui.showError({ message: 'No SQL query to format' });
      return;
    }

    const result = this.dbViewerService.formatQuery(this.sqlQuery);

    if (result.success && result.formatted) {
      this.sqlQuery = result.formatted;
      this.updateEditorValue(result.formatted);
      this.onQueryChange();
      TabSdk.ui.showSuccess({ message: 'SQL query formatted successfully' });
    } else {
      TabSdk.ui.showError({ message: result.error || 'Failed to format SQL query' });
    }
  }

  executeQuery(queryToExecute?: string): void {
    // Use provided query or fall back to the full query
    const query = queryToExecute || this.sqlQuery;

    if (!query.trim()) {
      TabSdk.ui.showError({ message: 'Please enter a SQL query to execute' });
      return;
    }

    // Validate query synchronously
    const errors = this.dbViewerService.validateQuery(query);
    if (errors.length > 0) {
      this.validationErrors = errors;
      this.hasValidationErrors = true;
      this.displayValidationErrors(errors);
      return;
    }

    // Clear grid modifications
    this.dbViewerService.clearGridModifications();
    this.dbViewerService.setOriginalQuery(query);

    this.isExecuting = true;
    this.dbViewerService.setGridUpdateState(GridUpdateState.EXECUTING_QUERY);
    this.queryResults = null;
    this.showResults = false;

    this.dbViewerService.executeQuery(query, this.parameters, this.schemaData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: QueryExecutionResponse) => {
          this.isExecuting = false;
          this.dbViewerService.setGridUpdateState(GridUpdateState.IDLE);
          this.queryResults = response;
          this.showResults = true;

          // Clear grid state
          setTimeout(() => {
            if (this.dbViewerService.hasGridModifications()) {
              this.onGridFilterChange([]);
              this.onGridSortChange([]);
              this.onGridGroupChange([]);
            }
          }, 100);

          if (response.success) {
            TabSdk.ui.showSuccess({
              message: `Query executed successfully: ${response.metadata.rowCount} rows in ${response.metadata.executionTime}s`
            });
          } else {
            TabSdk.ui.showError({
              message: response.error || 'Query execution failed'
            });
          }
        },
        error: (error) => {
          this.isExecuting = false;
          this.dbViewerService.setGridUpdateState(GridUpdateState.IDLE);
          TabSdk.ui.showError({ message: error.message || 'Error executing query' });
        }
      });
  }

  clearEditor(): void {
    TabSdk.ui.confirm({
      message: 'Are you sure you want to clear the editor?',
      title: 'Clear editor'
    }).then(confirmed => {
      if (confirmed) {
        this.showResults = false;
        this.sqlQuery = '';
        this.parameters = [];
        this.hasValidationErrors = false;
        this.updateEditorValue('');
      }
    });
  }

  /**
   * Handle query generation from schema explorer context menu
   */
  handleGenerateQuery(event: { query: string; execute: boolean }): void {
    // Replace the current query
    this.sqlQuery = event.query;
    this.updateEditorValue(event.query);

    // Auto-execute if requested
    if (event.execute && this.sqlQuery.trim()) {
      setTimeout(() => {
        this.executeQuery();
      }, 100);
    }
  }

  /**
   * Execute query from toolbar button
   * Checks if text is selected and executes only that portion
   */
  executeQueryFromButton(): void {
    if (!this.editor || this.isExecuting) return;

    // Check if user has selected text
    const selection = this.editor.getSelection();
    const selectedText = this.editor.getModel()?.getValueInRange(selection);

    if (selectedText && selectedText.trim()) {
      // Execute only selected text
      this.executeQuery(selectedText);
    } else {
      // Execute full query
      this.executeQuery();
    }
  }

  // ==================== GRID SYNCHRONIZATION ====================

  onGridFilterChange(filters: any[]): void {
    this.dbViewerService.updateGridFilters(filters);
    this.updateSQLFromGrid();
  }

  onGridSortChange(sorts: any[]): void {
    this.dbViewerService.updateGridSorts(sorts);
    this.updateSQLFromGrid();
  }

  onGridGroupChange(groups: any[]): void {
    this.dbViewerService.updateGridGroups(groups);
    this.updateSQLFromGrid();
  }

  private updateSQLFromGrid(): void {
    if (!this.dbViewerService.canUpdateFromGrid() || !this.sqlQuery.trim()) {
      return;
    }

    // If no grid modifications, restore original query
    if (!this.dbViewerService.hasGridModifications()) {
      const originalQuery = this.dbViewerService.getOriginalQuery();
      if (this.sqlQuery !== originalQuery) {
        this.dbViewerService.setGridUpdateState(GridUpdateState.UPDATING_FROM_GRID);
        this.sqlQuery = originalQuery;
        this.updateEditorValue(originalQuery);
        this.detectParameters();
        this.queryChangeSubject.next(this.sqlQuery);
        this.dbViewerService.setGridUpdateState(GridUpdateState.IDLE);
      }
      return;
    }

    // Build SQL with grid modifications
    this.dbViewerService.setGridUpdateState(GridUpdateState.UPDATING_FROM_GRID);

    const baseQuery = this.dbViewerService.getOriginalQuery() || this.sqlQuery;
    const newQuery = this.dbViewerService.buildSQLWithGridModifications(baseQuery);

    if (newQuery && newQuery !== this.sqlQuery) {
      this.sqlQuery = newQuery;
      this.updateEditorValue(newQuery);
      this.detectParameters();
      this.queryChangeSubject.next(this.sqlQuery);
    }

    this.dbViewerService.setGridUpdateState(GridUpdateState.IDLE);
  }

  private detectParameters(): void {
    this.parameters = this.dbViewerService.detectParameters(this.sqlQuery, this.parameters);
  }

  // ==================== TAB NAVIGATION ====================

  onTabChange(tab: 'sql' | 'visual' | 'json'): void {
    this.activeTab = tab;

    if (tab === 'sql') {
      setTimeout(() => {
        if (this.editor) {
          const currentValue = this.editor.getValue();
          if (currentValue !== this.sqlQuery) {
            this.updateEditorValue(this.sqlQuery);
          }
          this.editor?.layout();
        }
      }, 50);
    }

    if (tab === 'visual') {
      this.forceVisualParse = !this.forceVisualParse;
    }

    if (tab === 'json') {
      try {
        const jsonQuery = this.sqlParserService.sqlToJson(this.sqlQuery, this.schemaData);
        this.jsonInput = JSON.stringify(jsonQuery, null, 2);
      } catch (error: any) {
        this.jsonInput = '';
        TabSdk.ui.showError({ message: `Error converting SQL to JSON: ${error.message}` });
      }
    }
  }

  onVisualBuilderSQLChange(newSQL: string): void {
    if (newSQL !== this.sqlQuery) {
      this.sqlQuery = newSQL;

      if (this.editor) {
        const currentValue = this.editor.getValue();
        if (currentValue !== newSQL) {
          const position = this.editor.getPosition();
          this.updateEditorValue(newSQL);
          if (position) {
            const lineCount = this.editor.getModel()?.getLineCount() || 1;
            if (position.lineNumber <= lineCount) {
              this.editor.setPosition(position);
            } else {
              this.editor.setPosition({ lineNumber: lineCount, column: 1 });
            }
          }
          setTimeout(() => {
            this.editor?.layout();
          }, 0);
        }
      }

      this.detectParameters();
      this.queryChangeSubject.next(this.sqlQuery);
    }
  }

  onVisualBuilderWarning(warning: string): void {
    if (warning) {
      TabSdk.ui.showWarning({ message: warning });
    }
  }

  // ==================== VALIDATION UI ====================

  private updateValidationMarkers(): void {
    if (!this.editor || this.validationErrors.length === 0) {
      this.clearValidationMarkers();
      return;
    }

    const model = this.editor.getModel();
    if (!model) return;

    const markers: any[] = this.validationErrors.map(error => {
      const lineNumber = error.line || 1;
      let startColumn = error.column || 1;
      let endColumn = startColumn + 1;

      const lineContent = model.getLineContent(lineNumber);

      // Extract token for better highlighting
      const tokenMatch = error.message.match(/near\s+['"]([^'"]+)['"]/i);
      if (tokenMatch && tokenMatch[1]) {
        const token = tokenMatch[1].trim();
        const tokenIndex = lineContent.toLowerCase().indexOf(token.toLowerCase());
        if (tokenIndex !== -1) {
          startColumn = tokenIndex + 1;
          endColumn = tokenIndex + token.length + 1;
        }
      }

      return {
        severity: error.severity === 'error'
          ? this.monaco.MarkerSeverity.Error
          : this.monaco.MarkerSeverity.Warning,
        startLineNumber: lineNumber,
        startColumn: startColumn,
        endLineNumber: lineNumber,
        endColumn: endColumn,
        message: error.message,
        source: 'SQL Validation'
      };
    });

    this.monaco.editor.setModelMarkers(model, 'sql-validation', markers);
  }

  private clearValidationMarkers(): void {
    if (!this.editor) return;
    const model = this.editor.getModel();
    if (!model) return;
    this.monaco.editor.setModelMarkers(model, 'sql-validation', []);
  }

  private displayValidationErrors(errors: ValidationError[]): void {
    const errorMessages = errors.map(e =>
      `Line ${e.line || '?'}: ${e.message}`
    ).join('\n\n');

    this.queryResults = {
      success: false,
      data: [],
      metadata: {
        rowCount: 0,
        executionTime: 0,
        hasMore: false
      },
      error: errorMessages
    };
    this.showResults = true;

    TabSdk.ui.showError({ message: 'SQL Syntax Error: Please check the error details below.' });
  }

  // ==================== HELPER METHODS ====================

  private updateEditorValue(value: string): void {
    if (!this.editor) return;

    const model = this.editor.getModel();
    if (model && model.getLanguageId() !== 'sql') {
      this.monaco.editor.setModelLanguage(model, 'sql');
    }
    this.editor.setValue(value);
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const data = event.dataTransfer?.getData('text/plain');
    if (data && this.editor) {
      const position = this.editor.getPosition();
      if (position) {
        const insertText = data.startsWith('TABLE:') ? data.substring(6) : data;
        const range = new this.monaco.Range(
          position.lineNumber,
          position.column,
          position.lineNumber,
          position.column
        );
        const op = { range, text: insertText + ' ' };
        this.editor.executeEdits('drop-insert', [op]);
        const newPosition = new this.monaco.Position(
          position.lineNumber,
          position.column + insertText.length + 1
        );
        this.editor.setPosition(newPosition);
        this.editor.focus();
      }
    }
  }
}